import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { clearDraftBackup, saveDraftBackup } from "./draftBackup";

// Background autosave for a long form.
//
//   const autosave = useDraftAutosave({
//     enabled, values, save: async (values) => serverId, serialize, backupKey,
//   });
//
// `save` is called with the latest values a short while after the last edit
// (debounceMs), and never later than maxWaitMs after the first unsaved edit.
// It returns the server id, which is passed to the local backup so a restore
// prompt can tell "the server has this" from "the server never got it".
//
// status.state: off | idle | dirty | saving | saved | error | paused
//   paused = the server refused (e.g. a 409) with a message the user can act
//   on; the next edit tries again.
//
// resetKey: bump it to start a fresh session (the next values are "clean").
// settle(): wait for an in-flight save and drop the pending timer. Call it
//   before an explicit save so a draft is never created twice.
// flush():  save right now if anything is unsaved.
// markSaved(values): the caller saved explicitly; treat these values as clean.

export function useDraftAutosave({
    enabled,
    values,
    save,
    serialize,
    debounceMs = 2500,
    maxWaitMs = 30000,
    backupKey,
    // Change this (e.g. a counter bumped on every open) to start a fresh
    // session: the values seen next are treated as clean.
    resetKey,
}) {
    const [status, setStatus] = useState({ state: enabled ? "idle" : "off", savedAt: null, error: null });

    const snapshot = useMemo(() => {
        try {
            return serialize ? serialize(values) : JSON.stringify(values);
        } catch (_) {
            return "";
        }
    }, [values, serialize]);

    const latestValuesRef = useRef(values);
    const lastSavedRef = useRef(null);
    const timerRef = useRef(null);
    const firstDirtyAtRef = useRef(0);
    const inFlightRef = useRef(null);
    const dirtyWhileSavingRef = useRef(false);
    const serverIdRef = useRef(null);
    const enabledRef = useRef(enabled);
    const saveRef = useRef(save);
    const resetKeyRef = useRef(resetKey);

    latestValuesRef.current = values;
    enabledRef.current = enabled;
    saveRef.current = save;

    const clearTimer = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    const runSave = useCallback(async () => {
        clearTimer();
        if (!enabledRef.current) return;
        if (inFlightRef.current) {
            dirtyWhileSavingRef.current = true;
            return inFlightRef.current;
        }
        const valuesToSave = latestValuesRef.current;
        let snapshotToSave = "";
        try {
            snapshotToSave = serialize ? serialize(valuesToSave) : JSON.stringify(valuesToSave);
        } catch (_) {
            snapshotToSave = "";
        }
        setStatus((s) => ({ ...s, state: "saving", error: null }));
        const p = (async () => {
            try {
                const id = await saveRef.current(valuesToSave);
                if (id) serverIdRef.current = id;
                lastSavedRef.current = snapshotToSave;
                firstDirtyAtRef.current = 0;
                if (backupKey) saveDraftBackup(backupKey, valuesToSave, { serverId: serverIdRef.current });
                setStatus({ state: "saved", savedAt: Date.now(), error: null });
            } catch (err) {
                const message = (err && err.message) || "Autosave failed";
                const paused = !!(err && (err.code === "PAUSE" || err.status === 409));
                setStatus((s) => ({
                    ...s,
                    state: paused ? "paused" : "error",
                    error: message,
                }));
            } finally {
                inFlightRef.current = null;
            }
        })();
        inFlightRef.current = p;
        await p;
        if (dirtyWhileSavingRef.current) {
            dirtyWhileSavingRef.current = false;
            const current = serialize ? serialize(latestValuesRef.current) : JSON.stringify(latestValuesRef.current);
            if (current !== lastSavedRef.current) {
                timerRef.current = setTimeout(runSave, debounceMs);
            }
        }
    }, [backupKey, debounceMs, serialize]);

    // Schedule on every change.
    useEffect(() => {
        if (resetKey !== resetKeyRef.current) {
            resetKeyRef.current = resetKey;
            clearTimer();
            lastSavedRef.current = snapshot;
            serverIdRef.current = null;
            firstDirtyAtRef.current = 0;
            dirtyWhileSavingRef.current = false;
            setStatus({ state: enabled ? "idle" : "off", savedAt: null, error: null });
            return undefined;
        }
        if (!enabled) {
            clearTimer();
            setStatus((s) => (s.state === "off" ? s : { ...s, state: "off" }));
            return undefined;
        }
        if (snapshot === lastSavedRef.current) return undefined;
        if (lastSavedRef.current === null) {
            // First observation: treat the opening values as clean so an untouched
            // form is never saved as an empty draft.
            lastSavedRef.current = snapshot;
            setStatus((s) => (s.state === "off" ? { state: "idle", savedAt: null, error: null } : s));
            return undefined;
        }
        if (backupKey) saveDraftBackup(backupKey, values, { serverId: serverIdRef.current });
        setStatus((s) => (s.state === "saving" ? s : { ...s, state: "dirty" }));
        const now = Date.now();
        if (!firstDirtyAtRef.current) firstDirtyAtRef.current = now;
        const sinceFirst = now - firstDirtyAtRef.current;
        const delay = Math.max(0, Math.min(debounceMs, maxWaitMs - sinceFirst));
        clearTimer();
        timerRef.current = setTimeout(runSave, delay);
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [snapshot, enabled, resetKey]);

    useEffect(() => () => clearTimer(), []);

    const settle = useCallback(async () => {
        clearTimer();
        if (inFlightRef.current) {
            try {
                await inFlightRef.current;
            } catch (_) {
                // reported through status already
            }
        }
    }, []);

    const flush = useCallback(async () => {
        clearTimer();
        if (inFlightRef.current) {
            try {
                await inFlightRef.current;
            } catch (_) {
                // reported through status already
            }
        }
        if (snapshot !== lastSavedRef.current && enabledRef.current) {
            await runSave();
        }
    }, [runSave, snapshot]);

    const markSaved = useCallback(
        (savedValues, serverId) => {
            clearTimer();
            try {
                lastSavedRef.current = serialize ? serialize(savedValues) : JSON.stringify(savedValues);
            } catch (_) {
                lastSavedRef.current = null;
            }
            if (serverId) serverIdRef.current = serverId;
            firstDirtyAtRef.current = 0;
            if (backupKey) clearDraftBackup(backupKey);
            setStatus({ state: "saved", savedAt: Date.now(), error: null });
        },
        [backupKey, serialize]
    );

    const reset = useCallback(() => {
        clearTimer();
        lastSavedRef.current = null;
        serverIdRef.current = null;
        firstDirtyAtRef.current = 0;
        dirtyWhileSavingRef.current = false;
        setStatus({ state: enabledRef.current ? "idle" : "off", savedAt: null, error: null });
    }, []);

    return { status, settle, flush, markSaved, reset, serverId: serverIdRef.current };
}
